import { useState } from "react";
import { Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const SendEmailDialog = ({ open, onClose, doc, onSent }) => {
  const { t } = useI18n();
  const [to, setTo] = useState(doc?.client?.email || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!to.trim()) return toast.error(t("mail.noEmail"));
    setSending(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/email`, {
        to,
        subject: subject || undefined,
        message: message || undefined,
      });
      toast.success(`${t("mail.sent")} ${data.to}`);
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || t("mail.notConfigured"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="send-email-dialog">
        <DialogHeader>
          <DialogTitle>
            {t("mail.title")} · {doc?.number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t("mail.to")}</Label>
            <Input
              data-testid="email-to"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="cliente@email.it"
              className="mt-1.5 focus:ring-2 focus:ring-blue-600"
            />
          </div>
          <div>
            <Label>{t("mail.subject")}</Label>
            <Input
              data-testid="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={`${doc?.number}`}
              className="mt-1.5 focus:ring-2 focus:ring-blue-600"
            />
          </div>
          <div>
            <Label>{t("mail.message")}</Label>
            <Textarea
              data-testid="email-message"
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="mt-1.5 focus:ring-2 focus:ring-blue-600"
            />
          </div>
          <p className="text-xs text-slate-500">{t("mail.attach")}: {doc?.number}.pdf</p>
          <Button
            data-testid="email-send"
            onClick={send}
            disabled={sending}
            className="w-full gap-2 bg-blue-600 text-white hover:bg-blue-700"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            {t("mail.send")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SendEmailDialog;
